import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Button as ButtonPrimitive, buttonSizes, buttonVariants } from '../primitives/button.tsx';
import { Kbd } from './Kbd.tsx';
import { Spinner } from './Spinner.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps the label and width, disables the button and sets aria-busy. */
  loading?: boolean;
  /** Leading icon element (sized automatically). */
  icon?: ReactNode;
  /** Trailing icon element. */
  iconRight?: ReactNode;
  /** Shortcut hint inside the button, for example ['mod', 'enter']. */
  shortcut?: string[];
  children?: ReactNode;
}

/** Our variant names onto the shadcn Button's (primitives/button.tsx). */
const VARIANT = { primary: 'default', secondary: 'secondary', outline: 'outline', ghost: 'ghost', danger: 'destructive' } as const;
const SIZE = { sm: 'sm', md: 'default', lg: 'lg' } as const;

/** The full classes of a variant at the default size, for a link styled as a button. */
export const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: buttonVariants({ variant: VARIANT.primary }),
  secondary: buttonVariants({ variant: VARIANT.secondary }),
  outline: buttonVariants({ variant: VARIANT.outline }),
  ghost: buttonVariants({ variant: VARIANT.ghost }),
  danger: buttonVariants({ variant: VARIANT.danger }),
};

/** The size classes alone; merge after BUTTON_VARIANT with cn. */
export const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: buttonSizes[SIZE.sm],
  md: buttonSizes[SIZE.md],
  lg: buttonSizes[SIZE.lg],
};

/**
 * The one button, on the shadcn Button. Exactly one `primary` per surface; `danger` only on
 * the button that performs the destructive action. Labels are verb plus object.
 *
 * Loading keeps the label and the width: the spinner takes the leading icon's place, or, on a
 * button without one, sits centred over the (still announced) label.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  shortcut,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const spinner = <Spinner size={size === 'sm' ? 14 : 16} />;
  const overlay = loading && icon == null;
  return (
    <ButtonPrimitive
      type={type}
      variant={VARIANT[variant]}
      size={SIZE[size]}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={className}
      {...rest}
    >
      {loading && icon != null ? spinner : icon}
      {/* display: contents while idle, so the label lays out in the button's own flex row. */}
      <span className={overlay ? 'inline-flex items-center gap-1.5 opacity-0' : 'contents'}>
        {children}
        {iconRight}
        {/* No keyboard hint under a finger (a phone has no Ctrl Enter to press). */}
        {shortcut && <Kbd keys={shortcut} className={cn('ml-1 pointer-coarse:hidden', variant === 'primary' && !disabled && !loading && '[&_kbd]:kbd-on-accent')} />}
      </span>
      {overlay && <span className="absolute inset-0 inline-flex items-center justify-center">{spinner}</span>}
    </ButtonPrimitive>
  );
}
